import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../api/base';
import { getLocalized } from '../utils/localized';

interface Planting {
  id: number;
  name: { ru?: string; ky?: string; en?: string };
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  treesPlanted: number;
  plantedAt: string;
  eventId?: number | null;
  eventSlug?: string | null;
  participantsCount?: number | null;
  partnersCount?: number;
  areaHa?: number | null;
}

// Mapping between kg.svg region ids (KGB/KGGB/...) and DB region.code
// KGGO (город Ош) объединён с KGO (Ошская область) в один регион 'osh'
const SVG_ID_TO_CODE: Record<string, string> = {
  KGB: 'batken',
  KGGB: 'bishkek',
  KGC: 'chuy',
  KGY: 'issyk_kul',
  KGJ: 'jalal_abad',
  KGN: 'naryn',
  KGO: 'osh',
  KGGO: 'osh',
  KGT: 'talas'
};

interface RegionStat {
  id: number;
  code: string;
  slug: string;
  name: { ru?: string; ky?: string; en?: string };
  totalTrees: number;
  totalPlanted: number;
}

type Point = { lon: number; lat: number; x: number; y: number };

function makeAffine(p: Point[]) {
  const det = (m: number[]) =>
    m[0] * (m[4] * m[8] - m[5] * m[7]) -
    m[1] * (m[3] * m[8] - m[5] * m[6]) +
    m[2] * (m[3] * m[7] - m[4] * m[6]);
  const D = det([p[0].lon, p[0].lat, 1, p[1].lon, p[1].lat, 1, p[2].lon, p[2].lat, 1]);
  const solve = (v1: number, v2: number, v3: number) => [
    det([v1, p[0].lat, 1, v2, p[1].lat, 1, v3, p[2].lat, 1]) / D,
    det([p[0].lon, v1, 1, p[1].lon, v2, 1, p[2].lon, v3, 1]) / D,
    det([p[0].lon, p[0].lat, v1, p[1].lon, p[1].lat, v2, p[2].lon, p[2].lat, v3]) / D
  ];
  const X = solve(p[0].x, p[1].x, p[2].x);
  const Y = solve(p[0].y, p[1].y, p[2].y);
  return ([lon, lat]: [number, number]): [number, number] => [
    X[0] * lon + X[1] * lat + X[2],
    Y[0] * lon + Y[1] * lat + Y[2]
  ];
}

const TREE_ICON_SVG = `
<g transform="translate(-20,-20)">
  <path d='M14.675 28.0625C17.9473 28.0625 20.6 25.5105 20.6 22.3625C20.6 19.2145 17.9473 16.6625 14.675 16.6625C11.4027 16.6625 8.75 19.2145 8.75 22.3625C8.75 25.5105 11.4027 28.0625 14.675 28.0625Z' fill='#3E8E2A'/>
  <path d='M14.675 21.425C17.7817 21.425 20.3 19.0073 20.3 16.025C20.3 13.0427 17.7817 10.625 14.675 10.625C11.5684 10.625 9.05005 13.0427 9.05005 16.025C9.05005 19.0073 11.5684 21.425 14.675 21.425Z' fill='#3E8E2A'/>
  <path d='M20.825 19.925C24.0351 19.925 26.6375 17.4234 26.6375 14.3375C26.6375 11.2516 24.0351 8.75 20.825 8.75C17.6148 8.75 15.0125 11.2516 15.0125 14.3375C15.0125 17.4234 17.6148 19.925 20.825 19.925Z' fill='#3E8E2A'/>
  <path d='M26.375 23.375C29.0674 23.375 31.25 21.2763 31.25 18.6875C31.25 16.0987 29.0674 14 26.375 14C23.6826 14 21.5 16.0987 21.5 18.6875C21.5 21.2763 23.6826 23.375 26.375 23.375Z' fill='#3E8E2A'/>
  <path d='M22.8125 27.7625C25.5049 27.7625 27.6875 25.6638 27.6875 23.075C27.6875 20.4862 25.5049 18.3875 22.8125 18.3875C20.1201 18.3875 17.9375 20.4862 17.9375 23.075C17.9375 25.6638 20.1201 27.7625 22.8125 27.7625Z' fill='#3E8E2A'/>
  <path d='M18.8375 32C18.95 30.5 18.725 28.925 18.5 27.275C18.275 25.625 17.975 23.9375 17.975 22.2125C17.9375 20.4875 18.1625 18.725 18.7625 17.1125C19.325 15.5 20.1875 14.0375 21.2 12.725C20.3375 14.15 19.7 15.6875 19.3625 17.3C19.025 18.875 19.025 20.525 19.25 22.1C19.475 23.675 19.925 25.25 20.4125 26.8625C20.8625 28.475 21.35 30.1625 21.4625 32H18.8375Z' fill='#947151'/>
</g>
`.trim();

const PlantingsMap: React.FC = () => {
  const { t, i18n } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [plantings, setPlantings] = useState<Planting[]>([]);
  const [selected, setSelected] = useState<Planting | null>(null);
  const [regionsByCode, setRegionsByCode] = useState<Record<string, RegionStat>>({});
  const activePinRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ plantings: Planting[] }>(`${API_URL}/api/plantings`);
        setPlantings(data.plantings.filter(p => p.latitude != null && p.longitude != null));
      } catch (err) {
        console.error('Error fetching plantings:', err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ regions: RegionStat[] }>(`${API_URL}/api/regions/stats`);
        const map: Record<string, RegionStat> = {};
        data.regions.forEach(r => { map[r.code] = r; });
        setRegionsByCode(map);
      } catch (err) {
        console.error('Error fetching region stats:', err);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetch('/uploads/kg.svg');
      const text = await res.text();
      if (cancelled) return;

      const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
      const svg = svgRef.current;
      const wrap = wrapRef.current;
      const tip = tipRef.current;
      if (!svg || !wrap || !tip) return;

      // очистка
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      // референсные точки для аффинной проекции
      const refs: Point[] = Array.from(doc.querySelectorAll('#points circle')).map(c => {
        const [lat, lon] = (c.getAttribute('class') || '').split('|').map(Number);
        return {
          lon,
          lat,
          x: +(c.getAttribute('cx') || 0),
          y: +(c.getAttribute('cy') || 0)
        };
      });
      if (refs.length < 3) return;
      const project = makeAffine(refs);

      const NS = 'http://www.w3.org/2000/svg';

      // регионы
      const gRegions = document.createElementNS(NS, 'g');
      const lang = i18n.language || 'ru';
      doc.querySelectorAll('#features path').forEach(p => {
        const id = p.getAttribute('id') || '';
        const code = SVG_ID_TO_CODE[id];
        const stat = code ? regionsByCode[code] : undefined;
        const el = document.createElementNS(NS, 'path');
        el.setAttribute('d', p.getAttribute('d') || '');
        el.setAttribute('class', 'region');
        el.dataset.name = stat ? getLocalized(stat.name, lang) : '';
        el.dataset.trees = stat ? String(stat.totalPlanted || 0) : '';
        gRegions.appendChild(el);
      });
      svg.appendChild(gRegions);

      const treesShortLabel = t('plantings_map.trees_short', 'деревьев');
      gRegions.addEventListener('mousemove', e => {
        const path = e.target as SVGPathElement;
        if (!path.classList?.contains('region')) return;
        const trees = path.dataset.trees ? Number(path.dataset.trees) : 0;
        tip.innerHTML =
          (path.dataset.name || '') +
          (trees
            ? `<br><b style="color:#C7F08A">${trees.toLocaleString('ru-RU')}</b> ${treesShortLabel}`
            : '');
        const r = wrap.getBoundingClientRect();
        // сделать видимым чтобы измерить размеры
        tip.style.opacity = '1';
        const tipW = tip.offsetWidth;
        const tipH = tip.offsetHeight;
        const cursorX = e.clientX - r.left;
        const cursorY = e.clientY - r.top;
        // предпочитаем справа от курсора, если не влезает — слева
        let left = cursorX + 16;
        if (left + tipW > r.width - 8) left = cursorX - tipW - 16;
        if (left < 8) left = 8;
        let top = cursorY - 10 - tipH;
        if (top < 8) top = cursorY + 20;
        if (top + tipH > r.height - 8) top = r.height - tipH - 8;
        tip.style.left = left + 'px';
        tip.style.top = top + 'px';
      });
      gRegions.addEventListener('mouseleave', () => (tip.style.opacity = '0'));

      // пины
      const gPins = document.createElementNS(NS, 'g');
      svg.appendChild(gPins);

      plantings.forEach(d => {
        if (d.latitude == null || d.longitude == null) return;
        const [x, y] = project([d.longitude, d.latitude]);
        (d as any)._x = x;
        (d as any)._y = y;
        const g = document.createElementNS(NS, 'g');
        g.setAttribute('class', 'pin');
        g.setAttribute('style', `--tx:${x}px;--ty:${y}px;transform:translate(${x}px,${y}px)`);
        g.innerHTML = `
          <rect x="-20" y="-38" width="40" height="60" fill="transparent"/>
          <g transform="translate(0,2)" style="filter:drop-shadow(0 0 1.5px #fff) drop-shadow(0 0 1.5px #fff) drop-shadow(0 3px 4px rgba(0,0,0,.3))">
            <g transform="translate(0,0) scale(1.3)">
              ${TREE_ICON_SVG}
            </g>
          </g>
          <g transform="translate(0,-27)">
            <rect x="-24" y="-10.5" width="48" height="21" rx="10.5" fill="#0B7A43" stroke="#fff" stroke-width="2"/>
            <text y="3.5" text-anchor="middle" style="font:700 10px 'Golos Text',sans-serif;fill:#fff;pointer-events:none">${d.treesPlanted.toLocaleString('ru-RU')}</text>
          </g>
        `;
        g.addEventListener('mouseenter', () => gPins.appendChild(g));
        g.addEventListener('pointerdown', e => {
          e.stopPropagation();
          activePinRef.current = g;
          gPins.appendChild(g);
          setSelected(d);
        });
        gPins.appendChild(g);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [plantings, t, regionsByCode, i18n.language]);

  useEffect(() => {
    if (!selected) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.pin') || target.closest('[data-plant-card]')) return;
      setSelected(null);
      activePinRef.current = null;
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [selected]);

  // позиционируем карточку рядом с пином
  useEffect(() => {
    const card = cardRef.current;
    const wrap = wrapRef.current;
    const svg = svgRef.current;
    if (!card || !wrap || !svg || !selected) return;
    const scale = svg.getBoundingClientRect().width / 1000;
    const off = svg.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const px = (selected as any)._x * scale;
    const py = (selected as any)._y * scale + off;
    let left = px + 26;
    if (left + cw > wrap.clientWidth - 12) left = px - cw - 26;
    if (left < 12) left = 12;
    let top = py - ch / 2;
    if (top + ch > wrap.clientHeight - 12) top = wrap.clientHeight - ch - 12;
    if (top < 12) top = 12;
    card.style.left = left + 'px';
    card.style.top = top + 'px';
  }, [selected]);

  const openFestival = () => {
    if (selected?.eventSlug) {
      window.dispatchEvent(new CustomEvent('festival:activate', { detail: { slug: selected.eventSlug } }));
    }
    const el = document.getElementById('festivals');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSelected(null);
  };

  const regionRows = useMemo(
    () =>
      Object.values(regionsByCode)
        .map(r => ({
          id: r.code,
          name: getLocalized(r.name, i18n.language),
          trees: r.totalPlanted || 0
        }))
        .sort((a, b) => b.trees - a.trees),
    [regionsByCode, i18n.language]
  );

  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

  return (
    <section id="plantings-map" style={{ background: '#fff', padding: '64px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div style={{ marginBottom: 12 }}>
            <span className="section-tag">{t('plantings_map.tag', 'Наши посадки')}</span>
          </div>
          <h2 className="h2-center" style={{ margin: '0 0 8px' }}>
            {t('plantings_map.title')}
          </h2>
          <p className="map-hint" style={{ font: "400 15px 'Golos Text',sans-serif", color: '#5a6b4d', margin: 0 }}>
            {t('plantings_map.hint', 'Нажмите на дерево, чтобы увидеть детали посадки')}
          </p>
        </div>

        <div
          ref={wrapRef}
          className="map-wrap"
          style={{
            position: 'relative',
            borderRadius: 32,
            background: 'rgb(244, 246, 242)',
            padding: 20,
            boxSizing: 'border-box'
          }}
        >
          <svg
            ref={svgRef}
            viewBox="0 0 1000 492"
            style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
          />

          <div className="region-stats">
            {regionRows.map((r, i) => (
              <React.Fragment key={r.id}>
                {i > 0 && <div className="region-stats-divider" />}
                <div className="region-stat-item">
                  <div
                    style={{
                      font: "700 13px 'Montserrat Alternates', sans-serif",
                      color: '#0B7A43',
                      lineHeight: 1
                    }}
                  >
                    {r.trees.toLocaleString('ru-RU')}
                  </div>
                  <div
                    style={{
                      font: "400 10px 'Golos Text', sans-serif",
                      color: '#5a6b4d',
                      marginTop: 2,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {r.name}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div
            ref={tipRef}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              background: '#2D5016',
              color: '#fff',
              font: "500 13px/1.35 'Golos Text', sans-serif",
              textAlign: 'center',
              padding: '7px 14px',
              borderRadius: 14,
              whiteSpace: 'nowrap',
              zIndex: 4,
              transition: 'opacity 200ms ease'
            }}
          />
          {selected && (
            <div
              ref={cardRef}
              data-plant-card
              style={{
                position: 'absolute',
                width: 250,
                background: '#fff',
                borderRadius: 20,
                boxShadow: '0 16px 40px rgba(0,20,8,.25)',
                padding: '15px 17px',
                zIndex: 20,
                transition: 'opacity 200ms, transform 200ms'
              }}
            >
              <div style={{ font: "600 16px 'Golos Text', sans-serif" }}>
                {getLocalized(selected.name, i18n.language)}
              </div>
              <div style={{ font: "400 12.5px 'Golos Text', sans-serif", color: '#777', marginTop: 2 }}>
                {selected.locationName || ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                <StatBox value={selected.treesPlanted.toLocaleString('ru-RU')} label={t('plantings_map.trees_short', 'саженцев')} />
                <StatBox value={(selected.participantsCount ?? 0).toLocaleString('ru-RU')} label={t('plantings_map.participants', 'участников')} />
                <StatBox value={String(selected.partnersCount ?? 0)} label={t('plantings_map.partners', 'партнёров')} />
                <StatBox value={(selected.areaHa ?? 0).toLocaleString('ru-RU')} label={t('plantings_map.area_ha', 'площадь, га')} />
              </div>
              <div style={{ font: "400 12.5px 'Golos Text', sans-serif", color: '#777', marginTop: 10 }}>
                {t('plantings_map.date_label')}: {fmtDate(selected.plantedAt)}
              </div>
              <button
                type="button"
                onClick={openFestival}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  marginTop: 10,
                  background: '#5EA82E',
                  border: 0,
                  borderRadius: 9999,
                  padding: '9px 0',
                  color: '#fff',
                  font: "600 14px 'Golos Text', sans-serif",
                  cursor: 'pointer'
                }}
              >
                {t('plantings_map.more', 'Подробнее')}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const StatBox: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div style={{ background: '#DFEFCB', borderRadius: 12, padding: '8px 10px' }}>
    <div style={{ font: "700 17px 'Golos Text', sans-serif", color: '#0B7A43' }}>{value}</div>
    <div style={{ font: "400 11.5px 'Golos Text', sans-serif", color: '#777' }}>{label}</div>
  </div>
);

export default PlantingsMap;
